import React, { useState } from 'react';
import { XIcon, CheckIcon, FileTextIcon, FileSpreadsheetIcon, PrinterIcon, DownloadIcon } from 'lucide-react';
interface PrintOptionsModalProps {
  settlement: {
    id: string;
    dealerName: string;
    amount: number;
    currency: string;
  };
  onClose: () => void;
}
export const PrintOptionsModal: React.FC<PrintOptionsModalProps> = ({
  settlement,
  onClose
}) => {
  const [selectedDocuments, setSelectedDocuments] = useState({
    summary: true,
    receipt: true,
    invoice: false,
    ordersReport: false
  });
  const [orientation, setOrientation] = useState('portrait');
  const [paperSize, setPaperSize] = useState('a4');
  const [isPrinting, setIsPrinting] = useState(false);
  const [printSuccess, setPrintSuccess] = useState(false);
  const [saveAsPdf, setSaveAsPdf] = useState(false);
  const handleDocumentSelection = (document: string) => {
    setSelectedDocuments({
      ...selectedDocuments,
      [document]: !selectedDocuments[document]
    });
  };
  const handlePrint = () => {
    setIsPrinting(true);
    // Simulate print process
    setTimeout(() => {
      setIsPrinting(false);
      setPrintSuccess(true);
      // Close after showing success message
      setTimeout(() => {
        onClose();
      }, 1500);
    }, 2000);
  };
  const getDocumentIcon = (documentType: string) => {
    switch (documentType) {
      case 'ordersReport':
        return <FileSpreadsheetIcon className="h-5 w-5 text-green-600" data-id="element-626" />;
      default:
        return <FileTextIcon className="h-5 w-5 text-blue-600" data-id="element-627" />;
    }
  };
  const getDocumentTitle = (documentType: string) => {
    switch (documentType) {
      case 'summary':
        return 'Settlement Summary';
      case 'receipt':
        return 'Payment Receipt';
      case 'invoice':
        return 'Settlement Invoice';
      case 'ordersReport':
        return 'Orders Report';
      default:
        return documentType;
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-628">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-id="element-629">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-630">
          <h2 className="text-xl font-semibold text-gray-800" data-id="element-631">
            Print Settlement Documents
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-632">
            <XIcon className="h-5 w-5" data-id="element-633" />
          </button>
        </div>
        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-8rem)]" data-id="element-634">
          {printSuccess ? <div className="flex flex-col items-center justify-center py-8" data-id="element-635">
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-636">
                <CheckIcon className="h-6 w-6 text-green-600" data-id="element-637" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-638">
                {saveAsPdf ? 'Documents saved as PDF' : 'Documents sent to printer'}
              </h3>
              <p className="text-gray-500 text-center" data-id="element-639">
                {saveAsPdf ? 'The selected documents have been saved to your downloads folder' : 'The selected documents have been sent to your printer'}
              </p>
            </div> : <div className="space-y-6" data-id="element-640">
              <div data-id="element-641">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-642">
                  Settlement: {settlement.id}
                </h3>
                <div className="text-sm text-gray-500" data-id="element-643">
                  <p data-id="element-644">Dealer: {settlement.dealerName}</p>
                  <p data-id="element-645">
                    Amount: {settlement.currency} {settlement.amount.toFixed(2)}
                  </p>
                </div>
              </div>
              <div data-id="element-646">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-647">
                  Select Documents to Print
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-648">
                  {Object.keys(selectedDocuments).map(doc => <div key={doc} className={`border rounded-md p-4 cursor-pointer ${selectedDocuments[doc] ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => handleDocumentSelection(doc)} data-id="element-649">
                      <div className="flex items-center" data-id="element-650">
                        <input type="checkbox" checked={selectedDocuments[doc]} onChange={() => handleDocumentSelection(doc)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-651" />
                        <div className="ml-3 flex items-center" data-id="element-652">
                          {getDocumentIcon(doc)}
                          <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-653">
                            {getDocumentTitle(doc)}
                          </span>
                        </div>
                      </div>
                      <div className="mt-3 pl-7" data-id="element-654">
                        <div className="h-16 bg-gray-100 rounded-md flex items-center justify-center" data-id="element-655">
                          <span className="text-xs text-gray-500" data-id="element-656">
                            Document preview
                          </span>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-657">
                <div data-id="element-658">
                  <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-659">
                    Page Orientation
                  </h3>
                  <div className="flex space-x-4" data-id="element-660">
                    <label className="flex items-center" data-id="element-661">
                      <input type="radio" name="orientation" value="portrait" checked={orientation === 'portrait'} onChange={() => setOrientation('portrait')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-662" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-663">
                        Portrait
                      </span>
                    </label>
                    <label className="flex items-center" data-id="element-664">
                      <input type="radio" name="orientation" value="landscape" checked={orientation === 'landscape'} onChange={() => setOrientation('landscape')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-665" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-666">
                        Landscape
                      </span>
                    </label>
                  </div>
                </div>
                <div data-id="element-667">
                  <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-668">
                    Paper Size
                  </h3>
                  <select value={paperSize} onChange={e => setPaperSize(e.target.value)} className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm" data-id="element-669">
                    <option value="a4" data-id="element-670">A4</option>
                    <option value="letter" data-id="element-671">Letter</option>
                    <option value="legal" data-id="element-672">Legal</option>
                  </select>
                </div>
              </div>
              <div data-id="element-673">
                <label className="flex items-center" data-id="element-674">
                  <input type="checkbox" checked={saveAsPdf} onChange={() => setSaveAsPdf(!saveAsPdf)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-675" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-676">
                    Save as PDF instead of printing
                  </span>
                </label>
              </div>
            </div>}
        </div>
        {/* Footer */}
        {!printSuccess && <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3" data-id="element-677">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-678">
              Cancel
            </button>
            <button onClick={handlePrint} disabled={isPrinting || !Object.values(selectedDocuments).some(Boolean)} className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${isPrinting || !Object.values(selectedDocuments).some(Boolean) ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center`} data-id="element-679">
              {isPrinting ? <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-680">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-681"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-682"></path>
                  </svg>
                  {saveAsPdf ? 'Saving...' : 'Printing...'}
                </> : <>
                  {saveAsPdf ? <>
                      <DownloadIcon className="h-4 w-4 mr-2" data-id="element-683" />
                      Save as PDF
                    </> : <>
                      <PrinterIcon className="h-4 w-4 mr-2" data-id="element-684" />
                      Print
                    </>}
                </>}
            </button>
          </div>}
      </div>
    </div>;
};